import type { SitAnswerPatch } from "../../../../api/studentPracticePaperSit"
import type { PaperQuestionState, PaperSession } from "../types"
import { answerStatusFromHtml } from "./hasAnswerContent"

const patchFromQuestion = (q: PaperQuestionState): SitAnswerPatch => ({
  questionId: q.questionId,
  answerText: q.answerText,
  timeSpentSeconds: q.timeSpentSeconds,
  status: answerStatusFromHtml(q.answerText),
})

/** Build answer patches for questions that differ from a baseline (or all). */
export const buildAnswerPatches = (
  questions: PaperQuestionState[],
  baseline?: PaperQuestionState[] | null
): SitAnswerPatch[] => {
  if (!baseline?.length) {
    return questions.map(patchFromQuestion)
  }

  const byId = new Map(baseline.map((q) => [q.questionId, q]))
  const patches: SitAnswerPatch[] = []
  for (const q of questions) {
    const prev = byId.get(q.questionId)
    if (
      !prev ||
      prev.answerText !== q.answerText ||
      prev.timeSpentSeconds !== q.timeSpentSeconds ||
      prev.status !== q.status
    ) {
      patches.push(patchFromQuestion(q))
    }
  }
  return patches
}

export const requireSitCredentials = (
  session: PaperSession
): { sitToken: string; revision: number } => {
  const sitToken = (session.sitToken || "").trim()
  const revision = session.revision
  if (!sitToken) {
    throw new Error("Sit session token missing — resume the paper and try again")
  }
  if (revision === undefined || revision === null) {
    throw new Error("Sit session revision missing — refresh and try again")
  }
  return { sitToken, revision }
}

/** Clone question rows so baselines / snapshots cannot be mutated in place. */
export const cloneQuestions = (
  questions: PaperQuestionState[]
): PaperQuestionState[] => questions.map((q) => ({ ...q }))

/**
 * After autosave: take revision/token from the server, but keep local drafts.
 * Never replace answer fields with the response (it can be stale mid-typing).
 */
export const mergeAutosaveCredentials = (
  local: PaperSession,
  remote: PaperSession
): PaperSession => ({
  ...local,
  sitToken: remote.sitToken !== undefined ? remote.sitToken : local.sitToken,
  sitTokenExpiresAt:
    remote.sitTokenExpiresAt !== undefined
      ? remote.sitTokenExpiresAt
      : local.sitTokenExpiresAt,
  revision: remote.revision !== undefined ? remote.revision : local.revision,
  status: remote.status || local.status,
  lastActiveAt: remote.lastActiveAt || local.lastActiveAt,
  startedAt: remote.startedAt ?? local.startedAt,
})

/** Terminal ops (exit / abandon / submit) — server payload is authoritative. */
export const mergeSitServerSession = (
  local: PaperSession,
  remote: PaperSession
): PaperSession => ({
  ...local,
  ...remote,
  id: remote.id || local.id,
  paperId: remote.paperId || local.paperId,
  questions: remote.questions?.length ? remote.questions : local.questions,
  sitToken:
    remote.sitToken !== undefined ? remote.sitToken : local.sitToken,
  revision:
    remote.revision !== undefined ? remote.revision : local.revision,
})
